import { motion } from "motion/react";

// Main L logo ASCII art
export const L_LOGO_ASCII = `
██╗     
██║     
██║     
██║     
███████╗
╚══════╝
`;

export const L_LOGO_LARGE = `
    ██╗         
    ██║         
    ██║         
    ██║         
    ███████╗    
    ╚══════╝    
`;

export const DEATH_NOTE_BORDER = `
╔══════════════════════════════════════════════════════════════╗
║                                                              ║
╚══════════════════════════════════════════════════════════════╝
`;

export const LINK_ASCII = `
  ┌──────┐    ┌──────┐
  │ LINK │────│ LINK │
  └──────┘    └──────┘
`;

export const CHAIN_ASCII = `
○───○───○───○───○
`;

export const NOTEBOOK_ASCII = `
┌─────────────────────┐
│  ▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄  │
│  █ DEATH  NOTE █   │
│  ▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀  │
│                     │
│  ________________   │
│  ________________   │
│  ________________   │
│                     │
└─────────────────────┘
`;

export const STATS_BOX = `
┌─────────────────┐
│   ▓▓▓▓▓▓▓▓▓▓   │
│   ▓▓▓▓▓▓▓▓     │
│   ▓▓▓▓▓▓       │
│   ▓▓▓▓         │
└─────────────────┘
`;

export const QR_ASCII = `
█▀▀▀▀▀█ ▄▄▄▄▄ █▀▀▀▀▀█
█ ███ █ █▄█▄█ █ ███ █
█ ▀▀▀ █ ▀▀▀▀▀ █ ▀▀▀ █
▀▀▀▀▀▀▀ █ ▀ █ ▀▀▀▀▀▀▀
▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀
`;

export const CLICK_ASCII = `
    ╭─────╮
    │ ◉◉◉ │
    │ ◉◉  │
    │ ◉   │
    ╰─────╯
`;

export const LOCK_ASCII = `
   ┌───┐
   │ ▓ │
 ┌─┴───┴─┐
 │ ░░░░░ │
 │ ░░░░░ │
 └───────┘
`;

export const GLOBE_ASCII = `
    ╭──────╮
   ╱  ╲  ╱  ╲
  │ ─────── │
   ╲  ╱  ╲  ╱
    ╰──────╯
`;

export const ANALYTICS_ASCII = `
  │
  │    ▄
  │   ▄█▄
  │  ▄███▄
  │ ▄█████▄
  └──────────
`;

export const TERMINAL_CURSOR = "█";

interface AsciiDisplayProps {
  art: string;
  className?: string;
  animate?: boolean;
  delay?: number;
}

export function AsciiDisplay({
  art,
  className = "",
  animate = false,
  delay = 0,
}: AsciiDisplayProps) {
  if (animate) {
    return (
      <motion.pre
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay }}
        className={`font-mono text-[#9B87F5] whitespace-pre select-none ${className}`}
      >
        {art}
      </motion.pre>
    );
  }

  return (
    <pre
      className={`font-mono text-[#9B87F5] whitespace-pre select-none ${className}`}
    >
      {art}
    </pre>
  );
}

interface TypewriterTextProps {
  text: string;
  className?: string;
  speed?: number;
  delay?: number;
}

export function TypewriterText({
  text,
  className = "",
  speed = 50,
  delay = 0,
}: TypewriterTextProps) {
  return (
    <motion.span
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay }}
      className={className}
    >
      {text.split("").map((char, index) => (
        <motion.span
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: delay + index * (speed / 1000) }}
        >
          {char}
        </motion.span>
      ))}
    </motion.span>
  );
}

export function GlitchText({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.span
      className={`relative inline-block ${className}`}
      animate={{
        textShadow: [
          "0 0 0 transparent",
          "2px 0 0 #ff0000, -2px 0 0 #00ffff",
          "0 0 0 transparent",
        ],
      }}
      transition={{
        duration: 0.15,
        repeat: Infinity,
        repeatDelay: 3,
      }}
    >
      {children}
    </motion.span>
  );
}

export function BlinkingCursor({ className = "" }: { className?: string }) {
  return (
    <motion.span
      className={`inline-block ${className}`}
      animate={{ opacity: [1, 0] }}
      transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
    >
      {TERMINAL_CURSOR}
    </motion.span>
  );
}

export function AsciiBox({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <pre className="font-mono text-[#9B87F5]/30 text-xs absolute -top-2 -left-2 select-none">
        ╔═══
      </pre>
      <pre className="font-mono text-[#9B87F5]/30 text-xs absolute -top-2 -right-2 select-none">
        ═══╗
      </pre>
      {children}
      <pre className="font-mono text-[#9B87F5]/30 text-xs absolute -bottom-2 -left-2 select-none">
        ╚═══
      </pre>
      <pre className="font-mono text-[#9B87F5]/30 text-xs absolute -bottom-2 -right-2 select-none">
        ═══╝
      </pre>
    </div>
  );
}

export function MatrixRain({ className = "" }: { className?: string }) {
  const chars = "ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇﾍ01234567890";
  const columns = 20;

  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-[#9B87F5]/20 font-mono text-sm"
          style={{ left: `${(i / columns) * 100}%` }}
          initial={{ y: -100 }}
          animate={{ y: "100vh" }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            delay: Math.random() * 5,
          }}
        >
          {Array.from({ length: 20 }).map((_, j) => (
            <div key={j}>{chars[Math.floor(Math.random() * chars.length)]}</div>
          ))}
        </motion.div>
      ))}
    </div>
  );
}
